import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ChecklistItem } from '@prakash-software/shared';
import { fetchOnboarding, type OnboardingState } from '../lib/onboarding-api';
import { CHECKLIST_LABEL } from '../lib/onboarding-labels';
import styles from '../routes/Onboarding.module.css';

/** The setup items with their honest status: done, skipped or still to do. */
export function Checklist({ items, className = styles.checklist }: { items: ChecklistItem[]; className?: string }) {
  return (
    <ul className={className}>
      {items.map((item) => (
        <li key={item.key} className={styles.checkItem} data-status={item.status}>
          <span className={styles.mark} data-status={item.status} aria-hidden>
            {item.status === 'completed' ? '✓' : item.status === 'skipped' ? '–' : ''}
          </span>
          <span>
            {CHECKLIST_LABEL[item.key]}
            {item.status === 'skipped' ? (
              ' (skipped)'
            ) : (
              <span className={styles.srOnly}> ({item.status === 'completed' ? 'done' : 'to do'})</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Dashboard card for a self-serve workspace that isn't live yet. Hidden for
 * accounts without onboarding and once a real visitor has written in.
 */
export function SetupChecklist() {
  const [state, setState] = useState<OnboardingState | null>(null);

  useEffect(() => {
    let live = true;
    fetchOnboarding()
      .then((s) => live && setState(s))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  if (!state || state.activatedAt) return null;
  const done = state.checklist.filter((i) => i.status === 'completed').length;
  const pct = Math.round((done / state.checklist.length) * 100);

  return (
    <section className={styles.setupCard} aria-labelledby="setup-checklist-title">
      <div>
        <h2 id="setup-checklist-title">Finish setting up</h2>
        <p>
          {done} of {state.checklist.length} done. You are live once a real visitor messages you.
        </p>
        <div className={styles.progress} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Setup progress">
          <span style={{ width: `${pct}%` }} />
        </div>
        <p style={{ marginTop: 12 }}>
          <Link to="/onboarding">{state.nextStep ? 'Continue setup →' : 'Test and go live →'}</Link>
        </p>
      </div>
      <Checklist items={state.checklist} className={styles.setupItems} />
    </section>
  );
}
